// src/components/AddLapModal.js
import React, { useState, useEffect, useContext } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, IconButton, Autocomplete, Typography, MenuItem, Checkbox } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { addLap, getCircuits, getUserVehicles, updateLap, addPost } from '../utils/firestoreUtils';
import { sendEmailNotification } from '../utils/emailUtils';
import Compressor from 'compressorjs';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../utils/firebase';
import { AuthContext } from '../utils/AuthContext';

function FieldSection({ label }) {
  return (
    <div className="field-section">
      <div className="field-section-line" />
      <span className="field-section-label">{label}</span>
      <div className="field-section-line" />
    </div>
  );
}

function AddLapModal({ open, onClose, userId, lap, onDelete }) {
  const { user } = useContext(AuthContext);
  const [eventName, setEventName] = useState('');
  const [track, setTrack] = useState('');
  const [date, setDate] = useState('');
  const [bestLap, setBestLap] = useState('');
  const [proof, setProof] = useState('');
  const [circuits, setCircuits] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState('');
  const [selectedMap, setSelectedMap] = useState('');
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [images, setImages] = useState([]);
  const [mainImageIndex, setMainImageIndex] = useState(0);
  const [currentDigits, setCurrentDigits] = useState('');
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showConfirmDeleteImage, setShowConfirmDeleteImage] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [unsaved, setUnsaved] = useState(false);
  const [invalidFields, setInvalidFields] = useState({});
  const [postToCommunity, setPostToCommunity] = useState(false);
  const [communityDescription, setCommunityDescription] = useState('');
  // Tire fields
  const [tireModel, setTireModel] = useState('');
  const [tireSizeFront, setTireSizeFront] = useState('');
  const [tireSizeRear, setTireSizeRear] = useState('');
  const [circuitOptions, setCircuitOptions] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      const circs = await getCircuits();
      setCircuits(circs);
      const options = circs.flatMap(circ =>
        circ.layouts.map(lay => ({
          label: `${circ.name} - ${lay.layoutName}`,
          value: `${circ.id}-${lay.layoutName}`,
          map: lay.map,
        }))
      );
      setCircuitOptions(options);
      const vehs = await getUserVehicles(userId);
      setVehicles(vehs);
    };
    fetchData();
  }, [userId]);

  useEffect(() => {
    if (lap && circuitOptions.length > 0) {
      const option = circuitOptions.find(opt => opt.value === lap.track);
      setSelectedMap(option ? option.map : '');
    }
  }, [lap, circuitOptions]);

  useEffect(() => {
    if (lap) {
      setEventName(lap.eventName || '');
      setTrack(lap.track || '');
      setDate(lap.date || '');
      const formatted = formatLapTime(lap.bestLap) || '';
      setBestLap(formatted);
      setCurrentDigits(formatted.replace(/:/g, ''));
      setProof(lap.proof || '');
      setVehicleId(lap.vehicleId || '');
      setImages(lap.images || []);
      setMainImageIndex(lap.mainImageIndex || 0);
      setTireModel(lap.tireModel || '');
      setTireSizeFront(lap.tireSizeFront || '');
      setTireSizeRear(lap.tireSizeRear || '');
    } else {
      setEventName(''); setTrack(''); setDate(''); setBestLap(''); setCurrentDigits('');
      setProof(''); setVehicleId(''); setImages([]); setMainImageIndex(0);
      setTireModel(''); setTireSizeFront(''); setTireSizeRear('');
    }
    setUnsaved(false);
  }, [lap]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (images.length >= 5) { alert('Maximum of 5 images allowed.'); return; }
    new Compressor(file, {
      quality: 0.8, maxWidth: 1600, maxHeight: 1600,
      success(result) {
        const storageRef = ref(storage, `users/${userId}/laps/${file.name}`);
        uploadBytes(storageRef, result).then(() =>
          getDownloadURL(storageRef).then(url => { setImages(prev => [...prev, url]); setUnsaved(true); })
        );
      },
    });
  };

  const removeImage = (index) => {
    setImages(images.filter((_, idx) => idx !== index));
    setMainImageIndex(prev => prev === index ? 0 : prev > index ? prev - 1 : prev);
    setUnsaved(true);
  };

  const formatLapTime = (time) => {
    if (!time) return '';
    const s = parseFloat(time);
    const m = Math.floor(s / 60), sec = Math.floor(s % 60), ms = Math.round((s % 1) * 1000);
    return `${m}:${sec.toString().padStart(2, '0')}:${ms.toString().padStart(3, '0')}`;
  };

  const parseLapTime = (formatted) => {
    if (!formatted) return '';
    const parts = formatted.split(':');
    if (parts.length !== 3) return '';
    return (parseInt(parts[0]) * 60 + parseInt(parts[1]) + parseInt(parts[2]) / 1000).toString();
  };

  const formatDigits = (d) => {
    if (!d.length) return '';
    if (d.length === 1) return `0:00:00${d}`;
    if (d.length === 2) return `0:00:0${d}`;
    if (d.length === 3) return `0:00:${d}`;
    if (d.length === 4) return `0:${d.slice(0,1).padStart(2,'0')}:${d.slice(1)}`;
    if (d.length === 5) return `0:${d.slice(0,2)}:${d.slice(2)}`;
    if (d.length === 6) return `${d[0]}:${d.slice(1,3)}:${d.slice(3)}`;
    return `${d.slice(0,2)}:${d.slice(2,4)}:${d.slice(4,7)}`;
  };

  const handleLapTimeKeyDown = (e) => {
    if (e.key >= '0' && e.key <= '9') {
      if (currentDigits.length < 7) {
        const nd = currentDigits + e.key; setCurrentDigits(nd); setBestLap(formatDigits(nd)); setUnsaved(true);
      }
      e.preventDefault();
    } else if (e.key === 'Backspace') {
      const nd = currentDigits.slice(0, -1); setCurrentDigits(nd); setBestLap(formatDigits(nd)); setUnsaved(true);
      e.preventDefault();
    }
  };

  const handleClose = () => {
    if (unsaved && !window.confirm('Unsaved changes. Are you sure?')) return;
    onClose();
  };

  const handleSubmit = async () => {
    setInvalidFields({});
    const errors = {};
    if (!eventName) errors.eventName = true;
    if (!track) errors.track = true;
    if (!date) errors.date = true;
    if (!bestLap) errors.bestLap = true;
    if (!proof) errors.proof = true;
    if (!vehicleId) errors.vehicleId = true;
    if (!tireModel) errors.tireModel = true;
    if (postToCommunity && communityDescription.split(' ').length < 10) {
      setValidationError('Community description must be at least 10 words.'); return;
    }
    if (Object.keys(errors).length > 0) {
      setInvalidFields(errors); setValidationError('Please fill in all required fields.'); return;
    }
    if (!/^https?:\/\/.+/.test(proof)) {
      setInvalidFields({ proof: true }); setValidationError('Please provide a valid URL for Data or Video.'); return;
    }
    const selectedVehicle = vehicles.find(v => v.id === vehicleId);
    const lapData = {
      eventName, track, date, bestLap: parseLapTime(bestLap), proof, vehicleId,
      vehicleData: selectedVehicle ? { ...selectedVehicle } : null,
      images, mainImageIndex,
      tireModel, tireSizeFront, tireSizeRear,
      hasBeenReviewed: lap ? lap.hasBeenReviewed : false,
      isApproved: lap ? lap.isApproved : false,
    };
    if (lap) {
      await updateLap(userId, lap.id, lapData);
      onClose();
    } else {
      await addLap(userId, lapData);
      await sendEmailNotification('admin@email.com', 'New Lap Submitted');
      if (postToCommunity) {
        await addPost({ userId, userName: user?.displayName || 'User', type: 'lap', content: communityDescription, image: images[mainImageIndex] || null, timestamp: new Date(), likes: [], comments: [] });
      }
      setShowSuccessDialog(true);
    }
  };

  const selectedVehicle = vehicles.find(v => v.id === vehicleId);
  const isApproved = lap?.isApproved;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-xl)', m: { xs: 1, sm: 2 } } }}>

      {/* Header */}
      <DialogTitle sx={{ p: '20px 24px 16px', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', letterSpacing: '0.04em', lineHeight: 1 }}>
              {lap ? 'Edit Lap' : 'Add Lap'}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginTop: 4 }}>
              {isApproved ? 'Approved — some fields are locked' : 'All fields required unless noted'}
            </div>
          </div>
          <IconButton onClick={handleClose} size="small"><CloseIcon fontSize="small" /></IconButton>
        </div>
      </DialogTitle>

      <DialogContent sx={{ p: '20px 24px', overflowY: 'auto' }}>

        {/* ── EVENT ── */}
        <FieldSection label="Event" />
        <div className="field-row" style={{ marginBottom: 12 }}>
          <TextField label="Event Name" value={eventName} onChange={e => { setEventName(e.target.value); setUnsaved(true); }}
            error={invalidFields.eventName} size="small" fullWidth />
          <TextField type="date" label="Date" value={date} onChange={e => { setDate(e.target.value); setUnsaved(true); }}
            error={invalidFields.date} size="small" fullWidth InputLabelProps={{ shrink: true }} />
        </div>

        <Autocomplete
          options={circuitOptions}
          getOptionLabel={o => o.label}
          value={circuitOptions.find(o => o.value === track) || null}
          onChange={(_, val) => { setTrack(val ? val.value : ''); setSelectedMap(val ? val.map : ''); setUnsaved(true); }}
          disabled={isApproved}
          renderInput={params => <TextField {...params} label="Circuit" error={invalidFields.track} size="small" />}
          sx={{ mb: 1.5 }}
        />

        {/* Track map preview on white background */}
        {selectedMap && (
          <div className="modal-track-map">
            <img src={selectedMap} alt="Track map" />
          </div>
        )}

        {/* ── VEHICLE ── */}
        <FieldSection label="Vehicle" />
        <div className="field-row" style={{ marginBottom: 12 }}>
          <TextField select label="Vehicle" value={vehicleId}
            onChange={e => { setVehicleId(e.target.value); setUnsaved(true); }}
            error={invalidFields.vehicleId} disabled={isApproved} size="small" fullWidth>
            {vehicles.map(v => <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>)}
          </TextField>
          <TextField
            label="Best Lap (M:SS:SSS)"
            value={bestLap}
            onKeyDown={handleLapTimeKeyDown}
            error={invalidFields.bestLap}
            disabled={isApproved}
            size="small"
            fullWidth
            inputProps={{ style: { fontFamily: 'var(--font-mono)', fontWeight: 600, letterSpacing: '0.04em' } }}
            placeholder="0:00:000"
          />
        </div>

        {/* Vehicle mods display */}
        {selectedVehicle?.mods?.length > 0 && (
          <div className="mods-grid">
            {selectedVehicle.mods.map((mod, idx) => (
              <div key={idx} className="mod-item">
                <strong>{mod.category}:</strong>{mod.name}
              </div>
            ))}
          </div>
        )}

        {/* ── TIRES ── */}
        <FieldSection label="Tires" />
        <TextField label="Tire Model" value={tireModel}
          onChange={e => { setTireModel(e.target.value); setUnsaved(true); }}
          error={invalidFields.tireModel}
          disabled={isApproved}
          size="small" fullWidth
          sx={{ mb: 1.5 }}
          placeholder="e.g. RE71-RS"
        />
        <div className="field-row" style={{ marginBottom: 12 }}>
          <TextField label="Front Tire Size" value={tireSizeFront}
            onChange={e => { setTireSizeFront(e.target.value); setUnsaved(true); }}
            disabled={isApproved}
            size="small" fullWidth
            placeholder="e.g. 275/40/19"
          />
          <TextField label="Rear Tire Size" value={tireSizeRear}
            onChange={e => { setTireSizeRear(e.target.value); setUnsaved(true); }}
            disabled={isApproved}
            size="small" fullWidth
            placeholder="e.g. 275/40/19"
          />
        </div>

        {/* ── PROOF ── */}
        <FieldSection label="Proof" />
        <TextField label="Data or Video URL" value={proof}
          onChange={e => { setProof(e.target.value); setUnsaved(true); }}
          error={invalidFields.proof} disabled={isApproved}
          size="small" fullWidth sx={{ mb: 1.5 }}
          placeholder="https://..."
        />

        {/* ── IMAGES ── */}
        <FieldSection label="Images" />
        <label className="file-upload-label" htmlFor="lap-img-upload">
          <span>＋</span> Upload Image {images.length > 0 ? `(${images.length}/5)` : ''}
        </label>
        <input id="lap-img-upload" type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} />
        {images.length > 0 && (
          <div className="image-strip">
            {images.map((img, idx) => (
              <div key={idx} className="image-thumb">
                <img src={img} alt="" className={mainImageIndex === idx ? 'main-img' : ''} onClick={() => setMainImageIndex(idx)} />
                {mainImageIndex === idx && <div className="image-thumb-badge">M</div>}
                <button className="image-thumb-del" onClick={() => { setImageToDelete(idx); setShowConfirmDeleteImage(true); }}>✕</button>
              </div>
            ))}
          </div>
        )}

        {/* Community post toggle */}
        <label className="community-toggle">
          <input type="checkbox" checked={postToCommunity} onChange={e => setPostToCommunity(e.target.checked)} />
          <span className="community-toggle-label">Post to Community</span>
        </label>
        {postToCommunity && (
          <TextField label="What do you want to share? (10+ words)" multiline rows={3}
            value={communityDescription} onChange={e => setCommunityDescription(e.target.value)}
            fullWidth size="small" sx={{ mt: 1 }} />
        )}

        {/* Footer actions */}
        <div className="modal-actions">
          {lap ? (
            <button className="admin-btn-deny" style={{ padding: '8px 18px' }} onClick={() => setShowConfirmDelete(true)}>
              Delete Lap
            </button>
          ) : <div />}
          <Button onClick={handleSubmit} variant="contained" sx={{ textTransform: 'none', fontWeight: 700, px: 3 }}>
            {lap ? 'Save Changes' : 'Submit Lap'}
          </Button>
        </div>
      </DialogContent>

      {/* Confirm delete lap */}
      <Dialog open={showConfirmDelete} onClose={() => setShowConfirmDelete(false)}>
        <DialogTitle>Delete Lap</DialogTitle>
        <DialogContent><Typography>Are you sure you want to delete this lap?</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDelete(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button onClick={() => { onDelete(lap.id); setShowConfirmDelete(false); onClose(); }} color="error" variant="contained" sx={{ textTransform: 'none' }}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm delete image */}
      <Dialog open={showConfirmDeleteImage} onClose={() => setShowConfirmDeleteImage(false)}>
        <DialogTitle>Remove Image</DialogTitle>
        <DialogContent><Typography>Remove this image?</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDeleteImage(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button onClick={() => { removeImage(imageToDelete); setShowConfirmDeleteImage(false); }} color="error" variant="contained" sx={{ textTransform: 'none' }}>Remove</Button>
        </DialogActions>
      </Dialog>

      {/* Success */}
      <Dialog open={showSuccessDialog} onClose={() => { setShowSuccessDialog(false); onClose(); }}>
        <DialogTitle>🏁 Lap Submitted</DialogTitle>
        <DialogContent><Typography>An admin will review your lap shortly — thanks for racing with us!</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowSuccessDialog(false); onClose(); }} variant="contained" sx={{ textTransform: 'none' }}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* Validation error */}
      <Dialog open={!!validationError} onClose={() => { setValidationError(''); setInvalidFields({}); }}>
        <DialogTitle>Check Your Inputs</DialogTitle>
        <DialogContent><Typography>{validationError}</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => { setValidationError(''); setInvalidFields({}); }} sx={{ textTransform: 'none' }}>OK</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

export default AddLapModal;
